import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onPointerDownOutside={(e) => {
        if (e.target.closest('.pac-container') || e.target.closest('.pac-item') || 
            e.target.closest('[data-radix-popper-content-wrapper]') ||
            e.target.closest('[role="listbox"]')) {
          e.preventDefault();
        }
      }}
      onInteractOutside={(e) => {
        if (e.target.closest('.pac-container') || e.target.closest('.pac-item') ||
            e.target.closest('[data-radix-popper-content-wrapper]') ||
            e.target.closest('[role="listbox"]')) {
          e.preventDefault();
        }
      }}
      onOpenAutoFocus={(e) => e.preventDefault()}
      className={cn(
        // Base styles
        "fixed z-[100] grid gap-3 md:gap-4 border bg-background shadow-lg duration-200 pointer-events-auto",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        
        // MOBİL ZORUNLU BOŞLUK: env() çalışmasa bile en az 56px (pt-14) boşluk bırakır, çentik ve saat altına asla girmez
        "inset-0 w-full min-h-[100dvh] rounded-none p-4 pt-14 sm:pt-6 overflow-y-auto",
        
        // DESKTOP:
        "md:inset-auto md:min-h-0 md:h-auto md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-2xl md:p-6 md:max-h-[90vh] md:w-[95vw] md:max-w-4xl",
        "data-[state=closed]:slide-out-to-bottom md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%]",
        "data-[state=open]:slide-in-from-bottom md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%]",
        className
      )}
      {...props}>
      {children}
      
      {/* MOBİLDE SAATİN / ÇENTİĞİN NET BİÇİMDE ALTINA İNEN KAPAT BUTONU */}
      <DialogPrimitive.Close 
        className="absolute right-4 top-14 sm:top-4 z-[110] rounded-xl px-3 py-1.5 text-xs font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 border border-stone-200 shadow-sm flex items-center gap-1.5 active:scale-95 transition-all focus:outline-none"
      >
        <X className="h-4 w-4 text-stone-500 stroke-[2.5]" />
        <span>Kapat</span>
        <span className="sr-only">Kapat</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center md:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse md:flex-row md:justify-end md:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
